<template>
  <div class="text-left">
    <BreadcrumbsNav />

    <!-- Hero -->
    <v-row align="center" class="mb-8">
      <v-col cols="12" md="8">
        <h1 class="text-h4 font-weight-black mb-4">
          Espace Diététicien
          <br />
          <span class="primary--text">Suivez la nutrition, adaptez vos menus</span>
        </h1>
        <p class="text-subtitle-1 mb-4">
          Diététiciens, responsables nutrition, cuisines centrales — Labricot vous connecte aux données terrain pour
          travailler des menus à la fois conformes EGAlim et appétents pour les enfants. Enfin des retours concrets,
          plat par plat, en temps réel.
        </p>
        <div class="d-flex flex-wrap mb-4" style="gap: 8px">
          <v-chip color="primary" small outlined>Retours en temps réel</v-chip>
          <v-chip color="primary" small outlined>Statistiques d'appréciation</v-chip>
          <v-chip color="primary" small outlined>Plans d'actions nutrition</v-chip>
          <v-chip color="primary" small outlined>Conformité EGAlim</v-chip>
        </div>
        <div class="d-flex flex-wrap" style="gap: 12px">
          <v-btn color="primary" large :to="{ name: 'ContactPage' }">Contacter un conseiller</v-btn>
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <v-img
          src="/static/images/dashboard-dieteticien.png"
          contain
          max-width="100%"
          class="mx-auto rounded labricot-img"
        />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 1 — Feedback appréciation en temps réel -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <v-chip color="primary" outlined small class="mb-3">Diététicien · Cuisine centrale</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Recevez les retours sur l'appréciation des recettes</h2>
        <p>
          Fini les réunions trimestrielles comme seul retour terrain. Labricot centralise les appréciations des convives
          plat par plat — entrée, plat chaud, accompagnement, dessert — et vous les transmet automatiquement.
        </p>
        <ul class="mt-2 mb-4">
          <li>Résultats des votes enfants par service et par date</li>
          <li>Détail des plats signalés comme problématiques</li>
          <li>Raisons de non-consommation : quantité insuffisante, goût, autre</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : moins de refus d'aliments, recettes ajustées selon les vraies préférences
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <!-- TODO: remplacer src par la capture de la modale "Résultats — Votes enfants" (animateur) -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 2 — Statistiques & Ajustements nutritionnels -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <v-img src="/static/images/nutri1.png" contain max-width="100%" class="mx-auto rounded labricot-img" />
      </v-col>
      <v-col cols="12" md="6">
        <v-chip color="primary" outlined small class="mb-3">Analyse & Ajustements</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">
          Consultez les statistiques pour ajuster la composition nutritionnelle
        </h2>
        <p>
          Croisez les données d'appréciation avec les indicateurs nutritionnels pour adapter progressivement les
          grammages, les garnitures et les recettes. Travaillez avec les équipes cuisine sur des menus fondés sur des
          données, pas sur des intuitions.
        </p>
        <ul class="mt-2 mb-4">
          <li>Statistiques hebdomadaires et mensuelles de satisfaction</li>
          <li>Suivi par plat, par météo, par contexte (chaleur, repas festif)</li>
          <li>Exportation des données pour les réunions cuisine</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : menus mieux ciblés, gaspillage diminué par ajustement progressif
        </v-chip>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 3 — Plan d'actions Nutrition/Santé -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6" order-md="1" order="2">
        <v-chip color="primary" outlined small class="mb-3">Actions · Planification · Éducation</v-chip>
        <h2 class="text-h5 font-weight-bold mb-4">Créez vos plans d'actions nutrition et animez des challenges</h2>
        <p>
          Structurez et outillez un vrai projet pédagogique autour du "bien-manger". Créez des actions, suivez un plan
          et capitalisez les résultats d'une année à l'autre pour les partager à l'ensemble du réseau de cantines.
        </p>
        <ul class="mt-2 mb-4">
          <li>Création d'actions de recommandations nutrition/santé</li>
          <li>Suivi du plan d'actions avec les animateurs et éducateurs</li>
          <li>Challenges anti-gaspillage et enquêtes auprès des convives</li>
          <li>Capitalisation des projets au niveau de la commune ou du gestionnaire</li>
        </ul>
        <v-chip color="green darken-1" dark small>
          <v-icon left x-small>$check-line</v-icon>
          Bénéfice : éducation alimentaire mesurable, projets réutilisables d'une année à l'autre
        </v-chip>
      </v-col>
      <v-col cols="12" md="6" order-md="2" order="1">
        <!-- TODO: remplacer src par la capture du plan d'actions / création d'action -->
        <v-img src="" contain max-width="100%" class="mx-auto rounded labricot-img placeholder-img" />
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- Section 4 — Identification des risques alimentaires (roadmap) -->
    <v-row align="center" class="my-12">
      <v-col cols="12" md="6">
        <v-img
          src="/static/images/prediction-trouble.png"
          contain
          max-width="100%"
          class="mx-auto rounded labricot-img"
        />
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center mb-3" style="gap: 8px">
          <v-chip color="primary" outlined small>Diététicien & Éducateurs</v-chip>
          <v-chip color="orange darken-1" dark small>À venir</v-chip>
        </div>
        <h2 class="text-h5 font-weight-bold mb-4">Identification prédictive des risques alimentaires</h2>
        <p>
          La plateforme intégrera prochainement une analyse des signaux faibles pour identifier les risques de troubles
          alimentaires et permettre une adaptation préventive des menus et un signalement aux familles concernées.
        </p>
        <ul class="mt-2 mb-4">
          <li>Analyse des comportements alimentaires dans un cadre collectif</li>
          <li>Alertes préventives basées sur les changements de comportement</li>
          <li>Coordination avec les équipes médicales et les familles</li>
        </ul>
      </v-col>
    </v-row>

    <v-divider aria-hidden="true" role="presentation" class="my-10"></v-divider>

    <!-- CTA final -->
    <v-row justify="center" class="my-12 text-center">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold mb-4">Travaillez enfin avec des données terrain concrètes</h2>
        <p class="mb-6">
          Labricot vous connecte aux équipes de service pour adapter vos menus selon les vraies appréciations des
          convives.
        </p>
        <div class="d-flex flex-wrap justify-center" style="gap: 12px">
          <v-btn color="primary" large :to="{ name: 'ContactPage' }">Contacter un conseiller</v-btn>
          <v-btn color="primary" large outlined href="/creer-mon-compte">Créer mon compte</v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "NutritionistePage",
  components: { BreadcrumbsNav },
}
</script>

<style scoped>
.labricot-img {
  max-height: 400px;
  width: auto;
}
.placeholder-img {
  min-height: 220px;
  background-color: #f5f5f5;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
}
@media (max-width: 600px) {
  .labricot-img {
    max-width: 320px;
    height: auto;
    max-height: none;
  }
}
</style>
